import { useState, useEffect } from 'react';
import { cachedFetchJSON } from '../utils/fetchCache';

const CENSUS_BASE = 'https://api.census.gov/data';
const API_KEY = process.env.REACT_APP_CENSUS_API_KEY;
const CDC_BASE = 'https://data.cdc.gov/resource';

function parseVars(varExpr) {
  if (!varExpr) return [];
  return varExpr.split('+').map(v => v.trim());
}

function computeValue(obj, numVars, denVars, unit) {
  const numerator = numVars.reduce((sum, v) => sum + (parseFloat(obj[v]) || 0), 0);
  const denominator = denVars.length > 0
    ? denVars.reduce((sum, v) => sum + (parseFloat(obj[v]) || 0), 0)
    : null;

  let value;
  if (denominator !== null && denominator > 0) {
    value = unit === 'percent' ? (numerator / denominator) * 100 : numerator / denominator;
  } else if (denominator === null) {
    value = numerator;
  } else {
    value = null;
  }
  return value !== null && value < 0 ? null : value;
}

async function fetchCensusCountyValueForYear(indicator, state, countyFips, year) {
  const numVars = parseVars(indicator.numeratorVar);
  const denVars = parseVars(indicator.denominatorVar);
  const allVars = [...new Set([...numVars, ...denVars])];

  const params = new URLSearchParams({
    get: ['NAME', ...allVars].join(','),
    for: `county:${countyFips}`,
    in: `state:${state}`,
    ...(API_KEY ? { key: API_KEY } : {})
  });

  let rows;
  try {
    rows = await cachedFetchJSON(`${CENSUS_BASE}/${year}/acs/acs5?${params}`);
  } catch (_) { return null; }
  if (!Array.isArray(rows) || rows.length < 2) return null;

  const headers = rows[0];
  const obj = {};
  headers.forEach((h, i) => { obj[h] = rows[1][i]; });
  return computeValue(obj, numVars, denVars, indicator.unit);
}

async function fetchCDCCountyValue(indicator, state, countyFips) {
  if (!indicator.cdcViewId) return null;

  // countyFips is the 3-digit county FIPS; CDC uses 5-digit locationid
  const fullFips = `${state}${countyFips}`;
  const measureFilter = indicator.cdcMeasureId
    ? ` AND measureid='${indicator.cdcMeasureId}'`
    : '';
  // Build URL manually — URLSearchParams encodes '$' to '%24' which Socrata rejects
  const url =
    `${CDC_BASE}/${indicator.cdcViewId}.json` +
    `?$where=locationid='${fullFips}'${measureFilter}` +
    `&$select=data_value,year` +
    `&$limit=100`;

  let payload;
  try {
    payload = await cachedFetchJSON(url);
  } catch (_) { return null; }
  const rows = Array.isArray(payload) ? payload : [];
  if (!rows.length) return null;

  const years = [...new Set(rows.map(r => parseInt(r.year || r.Year, 10)).filter(Boolean))];
  years.sort((a, b) => b - a);
  const latestYear = years[0];

  const vals = rows
    .filter(r => parseInt(r.year || r.Year, 10) === latestYear)
    .map(r => parseFloat(r.data_value))
    .filter(v => !isNaN(v));

  if (!vals.length) return null;
  return vals.reduce((s, v) => s + v, 0) / vals.length;
}

/**
 * Fetches county-level aggregate values for each indicator for the comparison county.
 * For census indicators, also fetches per-year trend data.
 * Returns null immediately when comparisonFips is null.
 *
 * Returns:
 *   data: Map<indicatorId, { countyValue, trend: [{ year, value }] }> | null
 *   loading: boolean
 *   error: string | null
 */
export function useComparisonData(config, comparisonFips, selectedIndicators) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const state = config.org.geography.state;

  useEffect(() => {
    if (!comparisonFips) {
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);

      try {
        const results = new Map();

        const indicators = selectedIndicators ?? config.indicators;
        await Promise.all(indicators.map(async (indicator) => {
          if (indicator.source === 'census') {
            const years = indicator.years ?? [];
            const latestYear = years.length ? years[years.length - 1] : 2024;
            const fetchYears = years.length > 0 ? years : [latestYear];

            // Fetch all years in parallel — ~5 calls per indicator when a comparison county is selected
            const trendPoints = await Promise.all(
              fetchYears.map(async (year) => {
                const val = await fetchCensusCountyValueForYear(indicator, state, comparisonFips, year);
                return val !== null ? { year, value: val } : null;
              })
            );

            const trend = trendPoints.filter(Boolean);
            const countyValue = trend.find(p => p.year === latestYear)?.value ?? null;
            results.set(indicator.id, { countyValue, trend });

          } else if (indicator.source === 'cdc') {
            const countyValue = await fetchCDCCountyValue(indicator, state, comparisonFips);
            results.set(indicator.id, { countyValue, trend: [] });
          }
        }));

        if (!cancelled) {
          setData(results);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(`Comparison data failed to load: ${err.message}`);
          setLoading(false);
        }
      }
    }

    load();
    return () => { cancelled = true; };
  }, [config, comparisonFips, state, selectedIndicators]); // eslint-disable-line react-hooks/exhaustive-deps

  return { data, loading, error };
}
